import { useRef, useState } from "react";
import { MdDragIndicator } from "react-icons/md";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useTournament, useTournamentUpdate } from "../../TournamentContext";
import { bracketTeamBanner } from "../../../../functions/banners";
import gameTime from "../../../../functions/gameTime";
import { doc, updateDoc } from "firebase/firestore";
import { firestore } from "../../../../firebase/config";
import {
  INDEX_TO_COURT_NAME,
  SCORE_DELAY,
  INPUT_SAVE_ICON_DELAY,
} from "../../../../constants";
import updateBracket from "../../../../functions/updateBracket/updateBracket";
import SaveIndicator from "../../../../components/SaveIndicator";
import { TbExternalLink } from "react-icons/tb";

const Game = ({ gameId, bracketLevel, roundId, gameNumbers }) => {
  const { isUserOrganizer, games, teams, tournament, tournamentId } =
    useTournament();
  const { bracketTimes, bracket } = tournament;
  const { updateGames } = useTournamentUpdate();
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: gameId });

  const [gameSaveStatus, setGameSaveStatus] = useState(null);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const game = games[gameId];
  const gameNumber = gameNumbers[gameId] ?? null;

  const isAFinal = [
    "hayesLowerFinals",
    "hayesUpperFinals",
    "lowerFinals",
    "upperFinals",
    "grandFinals",
    "grandFinalsReset",
  ].includes(gameId);

  const debounceTimers = useRef({});
  const pendingUpdates = useRef({});

  const handleScoreChange = (team, value) => {
    if (!team || (!value && value !== 0)) {
      return;
    }

    const scoreKey = `${team}Score`;

    setGameSaveStatus("loading");

    if (!pendingUpdates.current[gameId]) {
      pendingUpdates.current[gameId] = {
        gameId,
        bracketLevel,
        roundId,
      };
    }

    pendingUpdates.current[gameId][scoreKey] = Number(value);

    if (debounceTimers.current[gameId]) {
      clearTimeout(debounceTimers.current[gameId]);
    }

    debounceTimers.current[gameId] = setTimeout(async () => {
      try {
        const updates = updateBracket(
          bracket,
          structuredClone(games),
          teams,
          { [gameId]: pendingUpdates.current[gameId] },
          updateGames
        );

        const bracketsRef = doc(firestore, "brackets", tournamentId);
        await updateDoc(bracketsRef, updates);

        updateGames(gameId, { [scoreKey]: value });
        setGameSaveStatus("success");

        delete pendingUpdates.current[gameId];
      } catch (error) {
        alert("Error saving bracket update:", error.message);
        setGameSaveStatus("error");
      } finally {
        setTimeout(() => {
          setGameSaveStatus(null);
        }, INPUT_SAVE_ICON_DELAY);
      }
    }, SCORE_DELAY);
  };

  const updateTeam = async (teamNumber, selectedTeamId) => {
    const bracketsRef = doc(firestore, "brackets", tournamentId);

    try {
      await updateDoc(bracketsRef, {
        [`${gameId}.${teamNumber}Id`]: selectedTeamId,
      });

      updateGames(gameId, { [`${teamNumber}Id`]: selectedTeamId });
    } catch (error) {
      alert(`Something went wrong: ${error.message}`);
    }
  };

  const teamName = (team) => {
    const teamId = game?.[`${team}Id`];
    const name = teams[teamId]?.name ?? teamId;

    if (name === "TBD") return "";
    if (name === "BYE") return "BYE";
    return name;
  };

  const renderTeam = (team) => {
    const name = teamName(team);
    const teamId = game?.[`${team}Id`];
    const group = teams[teamId]?.groupId;

    return (
      <div
        className={`${bracketTeamBanner(
          group
        )} w-full flex items-center justify-between p-1 gap-1`}
      >
        <select
          className="w-4 bg-transparent flex-1 text-sm font-medium text-gray-700 truncate mr-1 appearance-none"
          onChange={(e) => updateTeam(team, e.target.value)}
          defaultValue={teamId}
          disabled={!isUserOrganizer}
        >
          <option value={teamId}>{name || teamId}</option>
          {Object.entries(teams).map(([id, team]) => (
            <option key={id} value={id}>
              {team.name}
            </option>
          ))}
        </select>

        <input
          type="number"
          defaultValue={game?.[`${team}Score`] ?? ""}
          disabled={!isUserOrganizer}
          onChange={(e) => handleScoreChange(team, e.target.value)}
          className="w-8 text-center border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          aria-label={`Score for ${name}`}
          min={0}
        />
      </div>
    );
  };

  const displayGameTime = (bracketTimes) => {
    if (!gameNumber || !bracketTimes) return;
    return gameTime(
      gameNumber - 1,
      bracketTimes.startTimes[0],
      bracketTimes.gameLength,
      bracketTimes.interval,
      bracketTimes.numberOfCourts
    );
  };

  if (!tournament || !games) return null;

  return (
    <div
      id={gameId}
      data-testid={`bracket-game-${bracketLevel}-${roundId}-${gameId}`}
      ref={setNodeRef}
      style={style}
      className={`${
        gameId === "thirdPlace" ? "absolute top-20 right-48" : ""
      } w-48 my-1 flex items-center border rounded-lg shadow overflow-hidden relative`}
    >
      {/* {isUserOrganizer && (
        <MdDragIndicator
          {...attributes}
          {...listeners}
          className="w-6 h-8 text-gray-500 cursor-grab"
        />
      )} */}

      <div className="absolute -top-0.5 left-1 text-xs text-stone-500 font-semibold">
        {gameNumber}
      </div>

      <div className="w-full flex flex-col">
        {renderTeam("team1")}
        <div className="absolute top-7 right-10">
          <SaveIndicator saveStatus={gameSaveStatus} />
        </div>
        {renderTeam("team2")}
      </div>

      {bracketTimes.startTimes[0] && (
        <>
          {bracketTimes.numberOfCourts > 1 && (
            <div className="absolute -bottom-0.5 left-1 text-xs text-stone-500">
              {isAFinal
                ? "A"
                : INDEX_TO_COURT_NAME[gameNumber % bracketTimes.numberOfCourts]}
            </div>
          )}
          <div className="absolute -top-0.5 right-10 text-xs text-stone-500 font-light">
            {displayGameTime(bracketTimes)}
          </div>
        </>
      )}

      {game.hyperlink && (
        <a target="_blank" rel="noopener noreferrer" href={game.hyperlink}>
          <TbExternalLink className="h-3 w-3 absolute bottom-1 right-10 text-stone-600 cursor-pointer" />
        </a>
      )}
    </div>
  );
};

export default Game;
